import { useState, useEffect, useRef, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginSupersededError } from '../lib/loginSuperseded';
import OAuthButtons from '../components/OAuthButtons';
import PasswordInput from '../components/PasswordInput';
import { getOnboardingPathForTier } from '../lib/tier';
import { apiFetch, ApiError } from '../lib/api';
import { isSafeRedirect } from '../lib/redirectGuard';
import { resolveLandingRoute } from '../lib/landingRoute';
import { decodeJwtPayload } from '../lib/decodeJwtPayload';
import { TENANT_ACCESS_REVOKED, TENANT_ACCESS_REVOKED_MESSAGE } from '../lib/tenantAccessRevoked';
import { ACTION_SESSION_OUT_OF_SCOPE_LOGIN_ERROR, ACTION_SESSION_OUT_OF_SCOPE_MESSAGE } from '../lib/actionSessionOutOfScope';

function BlueprintMark({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="10" fill="#7C3AED" />
      <path d="M12 32 L24 26 L36 32 L24 38 Z" fill="white" opacity="0.22" />
      <path d="M12 26 L24 20 L36 26 L24 32 Z" fill="white" opacity="0.55" />
      <path d="M12 20 L24 14 L36 20 L24 26 Z" fill="white" opacity="0.9" />
      <circle cx="36" cy="12" r="5" fill="#EC4899" />
    </svg>
  );
}

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: 44,
  background: 'var(--surface-alt)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '0 14px',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
};

// PR #3275 R2 — one generation counter for every LoginPage instance. Each
// submit takes the next value; a submit whose value is no longer current (a
// newer submit started, possibly in a remounted LoginPage after the user went
// to Sign up and back) is ABANDONED: it must not navigate, show an error, or
// reset the session a newer login installed. Module-level on purpose: a
// per-instance ref would not see a newer attempt in a remounted page.
let loginGeneration = 0;

export default function LoginPage() {
  const { login, resetSession } = useAuth();
  // An unmounted LoginPage's in-flight submit is abandoned too (PR #3275 R2).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(() => {
    const oauthError = searchParams.get('error');
    const provider = searchParams.get('provider');
    if (oauthError === 'email_exists') return 'An account with this email already exists. Please sign in using your original sign-in method (password, Google, or GitHub).';
    if (oauthError === 'email_required') return `Your ${provider ?? 'social'} account has no public email. Please add one and try again.`;
    if (oauthError === 'oauth_failed') return 'Social sign-in failed. Please try again.';
    // oauth_not_configured: the API has no client for that provider. Before
    // #3039 this code rendered nothing.
    if (oauthError === 'oauth_not_configured') return "Social sign-in isn't available for this provider. Please sign in with your email and password.";
    // #3039 — the API's startup check found this provider's callback pointing
    // at another deployment and disabled it.
    // #3229 Story B — apiFetch sends a user here when the API refused their
    // token because their access to the workspace was removed.
    if (oauthError === 'tenant_access_revoked') return TENANT_ACCESS_REVOKED_MESSAGE;
    // #3248 — apiFetch sends a magic-link action session here when it was used
    // outside its one action (the deep link rides along as ?redirect=).
    if (oauthError === ACTION_SESSION_OUT_OF_SCOPE_LOGIN_ERROR) return ACTION_SESSION_OUT_OF_SCOPE_MESSAGE;
    if (oauthError === 'oauth_misconfigured') return 'Social sign-in with this provider is temporarily unavailable because of a server configuration problem. Please sign in with your email and password, or try again later.';
    return '';
  });
  // ONB-FIX.D9 (#1081) — ResetPasswordPage redirects here with ?reset=success
  // after a successful password change. Show a one-time positive banner; no
  // auto-login (Q12) — the user signs in with the new password. Snapshot the
  // query-param value at mount so the banner survives the URL strip below.
  const [resetSuccess] = useState(() => searchParams.get('reset') === 'success');
  const [loading, setLoading] = useState(false);

  // ── R1 MEDIUM-2 (adversarial 2026-05-25) — strip ?reset=success from
  // the URL after capturing it into state. Without this, a user who
  // bookmarks or shares the post-reset /login URL would re-show the
  // banner on every visit. The banner carries no token but advertises
  // that THIS account recently had its password reset from THIS URL —
  // a low-impact information leak we avoid by paying one replaceState.
  // Pairs with the equivalent strip in ResetPasswordPage (R1 CRITICAL-2).
  useEffect(() => {
    if (resetSuccess) {
      window.history.replaceState({}, '', '/login');
    }
    // Intentional empty deps — fires once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const attempt = ++loginGeneration;
    const isCurrent = () => mountedRef.current && attempt === loginGeneration;
    // Every navigation this attempt makes goes through here: an abandoned
    // attempt navigates nowhere.
    const go = (to: string, opts?: { replace?: boolean }) => {
      if (!isCurrent()) return;
      if (opts) navigate(to, opts);
      else navigate(to); // same call shape as before the wrapper
    };
    setLoading(true);
    setError('');
    try {
      const installedToken = await login(email, password);

      // ONB.B2-FIX.D1 — honor ?redirect= for magic-link round trips.
      // When the user arrived at /login because a magic-link page
      // (SetupAcceptPage, AcceptInvitePage, etc.) redirected them with
      // a ?redirect=<token-bearing path> param, navigate them back to
      // that page after login so the token reaches the accept handler.
      // Skip the onboarding-wizard auto-routing below — the user
      // explicitly came here via a magic link and is trying to do
      // something specific.
      //
      // Open-redirect guard extracted to apps/web/src/lib/redirectGuard.ts
      // (ONB.B2-FIX.D2) — the same predicate now applies at LoginPage,
      // RegisterPage, and EmailVerificationPage. See the utility's
      // docstring for the attack-class enumeration.
      const redirect = searchParams.get('redirect');
      if (isSafeRedirect(redirect)) {
        go(redirect, { replace: true });
        return;
      }

      // #2220 — CLIENT-only landing, decided BEFORE ever touching
      // /tenants/me/membership, not as a fallback after it fails.
      //
      // resolveNonClientDefaultTenantId (auth.ts, per #1608) deliberately
      // excludes CLIENT-role tenant_members rows when picking the JWT's
      // tenantId, so an account with no OTHER (non-CLIENT) membership gets a
      // JWT with no tenantId claim at all. GET /tenants/me/membership
      // requires that claim to resolve anything (tenants.ts:179-183 404s
      // immediately without it) — it is therefore GUARANTEED to 404 for such
      // an account, not just likely to. Reading the claim client-side (UI
      // routing only, never an authorization decision — see
      // decodeJwtPayload.ts's own docstring) lets this branch skip that
      // call outright instead of calling it and handling the failure.
      //
      // Checking tenantId ABSENCE first — rather than falling back to a
      // client-stakeholder check only after membership fails — also protects
      // a dual-role account: a real TENANT_ADMIN (or STUDENT/FACULTY_
      // SUPERVISOR) who separately holds client-stakeholder access on
      // another project still has a tenantId in their JWT, so they never
      // enter this branch at all and keep the ordinary membership-based
      // routing below, unaffected.
      const jwt = localStorage.getItem('blueprint_token');
      const claims = jwt ? decodeJwtPayload<{ tenantId?: string }>(jwt) : null;

      if (!claims?.tenantId) {
        try {
          const { projects: clientProjects } = await apiFetch<{
            projects: Array<{ projectId: string; projectName: string; domain: string | null; stakeholderId: string }>;
          }>('/client/my-projects');

          if (clientProjects.length === 1) {
            go(`/client/dashboard?projectId=${clientProjects[0].projectId}`);
            return;
          }
          if (clientProjects.length > 1) {
            // #2220 R2 — no unique constraint on
            // project_stakeholders(userId, projectId); a client can hold
            // ACTIVE access on more than one project. Route to the picker
            // rather than guessing which one.
            go('/client/select-project');
            return;
          }
          if (clientProjects.length === 0) {
            // #2331 — not a project_stakeholders row either. Before falling
            // through to the ordinary /projects-default routing below
            // (which 404s for this account exactly as /tenants/me/membership
            // does — no tenantId claim), check for the OTHER no-tenant-
            // members shape: a project_members-only account (e.g. a
            // CLIENT_SPONSOR invited via the project-team flow instead of
            // the stakeholder flow, or a PM/BUSINESS_ANALYST/etc project
            // team member with no tenant_members row). This runs ONLY when
            // the stakeholder check above found nothing, so a dual
            // stakeholder+project-member account still routes to its
            // existing, richer /client/dashboard destination above,
            // unaffected.
            try {
              const { projects: memberProjects } = await apiFetch<{
                projects: Array<{ projectId: string; projectName: string; domain: string | null; projectMemberId: string }>;
              }>('/client/my-member-projects');
              if (memberProjects.length > 0) {
                go('/member/dashboard');
                return;
              }
              // APPROVE.1a (#3183, ADJ-4) — a client user (CLIENT-role
              // membership) with no project shared yet, e.g. a partner-managed
              // client contact before the partner creates the first project.
              // Without this they fell through to an empty /projects page.
              try {
                const { tenants: memberships } = await apiFetch<{ tenants: Array<{ role: string }> }>('/auth/tenants');
                if (memberships.some((m) => m.role === 'CLIENT')) {
                  go('/client/select-project');
                  return;
                }
              } catch {
                // Fall through to the ordinary routing below.
              }
              // 0 results here too — not a project_members-only account
              // either — fall through to the ordinary routing below.
            } catch {
              // Not a project_members-only account either, or the check
              // itself failed — fall through to the ordinary routing below.
            }
          }
        } catch {
          // Not a client account either, or the check itself failed — fall
          // through to the ordinary routing below, unchanged from before
          // #2220.
        }
      }

      // Check onboarding status — redirect TENANT_ADMINs with incomplete setup
      // into the tier-appropriate wizard (STARTER/GROWTH → /onboarding/sme,
      // PLATFORM_PARTNER → /partner/onboarding, etc.). If the membership
      // lookup fails for any reason we fall through to /projects so we
      // never block the user on sign-in; ProjectsPage has its own banner
      // that will nudge incomplete admins toward the correct wizard.
      //
      // Hotfix 2026-05-24: use apiFetch (which prepends API_BASE) instead of
      // a raw fetch with a relative path. The raw `fetch('/api/...')` was
      // interpreted as same-origin → resolved to `app.blueprintsdlc.com/api/...`
      // → SPA static fallback returned index.html → silent failure that
      // routed everyone to /projects regardless of onboarding status. Per
      // [[feedback_match_neighbour_conventions]] this matches the
      // useMyTenant hook's apiFetch pattern and removes the divergence.
      let destination = '/projects';
      // #3269 — the follow-ups below are made with the just-issued token, whose
      // workspace claim the API checks. If membership was removed between
      // /auth/login and here, they answer 401 TENANT_ACCESS_REVOKED. Each
      // catch below fails open by design, so the refusal is recorded here and
      // checked before the final navigate: stop on /login with the message
      // instead of continuing to a protected page.
      let accessRevoked = false;
      const followUp = async <T,>(path: string): Promise<T> => {
        try {
          return await apiFetch<T>(path);
        } catch (followUpErr) {
          if (followUpErr instanceof ApiError && followUpErr.body?.code === TENANT_ACCESS_REVOKED) accessRevoked = true;
          throw followUpErr;
        }
      };
      try {
        const membership = await followUp<{
          role: string;
          onboardingStatus: string;
          subscriptionTier: string;
          // DASH.C1 (#825) — landing routing inputs (optional: tolerate an older
          // server that doesn't yet surface them → fall through to /projects).
          defaultLandingPage?: string;
          hasSeenLandingModal?: boolean;
          lastProjectId?: string | null;
          // ACT.A8 (#1709) Q4 — surfaced by GET /tenants/me/membership
          // (checks the SEPARATE platform_admins table, not tenantMembers.role
          // — see tenants.ts). Optional: tolerate an older server that
          // doesn't yet surface it → treated as not-a-platform-admin below.
          isPlatformAdmin?: boolean;
        }>('/tenants/me/membership');

        // ACT.A8 (#1709) Q4, narrowed per R1/R2 follow-up fix — "Platform
        // Admin (AVR) skips all onboarding flows" applies ONLY to the new
        // academic-tier hooks this story adds (the ACADEMIC-tier admin
        // wizard trigger below, and the STUDENT/FACULTY_SUPERVISOR tour
        // branch further down). It must NOT suppress the pre-existing
        // SME/Individual/Partner incomplete-onboarding redirect for a
        // platform admin who is ALSO a real TENANT_ADMIN of a NON-academic
        // tenant they provisioned (same admin-provisioned pattern as
        // academic-tenant.service.ts's createAcademicTenant) — that
        // tenant's onboarding must still complete exactly as before this
        // story. Scoped by requiring subscriptionTier === 'ACADEMIC' in
        // addition to isPlatformAdmin, not a bare isPlatformAdmin check.
        const skipAcademicOnboarding = membership.isPlatformAdmin === true;

        if (
          (membership.role === 'TENANT_ADMIN' || membership.role === 'ADMIN') &&
          membership.onboardingStatus !== 'COMPLETE' &&
          !(skipAcademicOnboarding && membership.subscriptionTier === 'ACADEMIC')
        ) {
          // Incomplete-setup admins go to onboarding first — takes precedence
          // over the landing preference. onboardingStatus is a closed 3-value
          // set (INCOMPLETE | IN_PROGRESS | COMPLETE); denylisting the single
          // terminal state catches both not-done statuses (#1599 — password
          // solo-signup sets IN_PROGRESS, which `=== 'INCOMPLETE'` missed).
          destination = getOnboardingPathForTier(membership.subscriptionTier);
        } else if (
          (membership.role === 'STUDENT' || membership.role === 'FACULTY_SUPERVISOR') &&
          !skipAcademicOnboarding
        ) {
          // ACT.A8 (#1709) Q4 — first-login tour redirects for the two new
          // onboarding flows. ADDITIVE branch: does not touch the
          // TENANT_ADMIN branch above or the hasSeenLandingModal/
          // resolveLandingRoute branches below.
          //
          // Merge note (rebase onto ACT.A5 #1764, 2026-07-20 founder-review
          // merge fix): ACT.A5 independently added its own
          // FACULTY_SUPERVISOR forced-cohort-dashboard-landing override to
          // this same file — exactly the collision this comment originally
          // flagged as a risk before either story had merged. Composed as:
          // the onboarding-tour check below runs FIRST — a supervisor who
          // hasn't completed their first-login tour sees it before
          // anything else, same precedence the TENANT_ADMIN branch above
          // gives incomplete setup. Only once onboarding is COMPLETE does
          // ACT.A5's forced cohort-dashboard landing take over. STUDENT has
          // no ACT.A5 override and falls through this branch's own
          // hasSeenLandingModal/resolveLandingRoute path unchanged.
          const flowType = membership.role === 'STUDENT' ? 'STUDENT' : 'FACULTY_SUPERVISOR';
          try {
            const progress = await followUp<{ completedAt: string | null }>(`/api/onboarding/${flowType}`);
            if (!progress.completedAt) {
              destination = flowType === 'STUDENT' ? '/onboarding/student' : '/onboarding/faculty-supervisor';
            } else if (membership.role === 'FACULTY_SUPERVISOR') {
              // ACT.A5 (#1706) Q7 — forced override: the cohort dashboard
              // is a role fact, not a user-overridable DASH.C1 preference
              // (unlike developer/partner/client's soft pre-selection
              // default below). Takes precedence over the first-login
              // modal too — a supervisor never has a "choose your landing
              // page" moment. Falls through to the normal DASH.C1
              // resolution (not /projects directly) if the supervisor has
              // no cohort yet.
              try {
                const { cohorts } = await followUp<{ cohorts: Array<{ id: string }> }>('/api/academic/my-cohorts');
                if (cohorts.length > 0) {
                  destination = `/academic/cohorts/${cohorts[0].id}/dashboard`;
                } else {
                  destination = resolveLandingRoute(membership.defaultLandingPage, membership.lastProjectId ?? null);
                }
              } catch {
                destination = resolveLandingRoute(membership.defaultLandingPage, membership.lastProjectId ?? null);
              }
            } else if (membership.hasSeenLandingModal === false) {
              destination = '/dashboard?firstLogin=true';
            } else {
              destination = resolveLandingRoute(membership.defaultLandingPage, membership.lastProjectId ?? null);
            }
          } catch {
            // Fail open, same posture as the outer try/catch — never block
            // sign-in on the onboarding-progress lookup.
            destination = resolveLandingRoute(membership.defaultLandingPage, membership.lastProjectId ?? null);
          }
        } else if (membership.hasSeenLandingModal === false) {
          // DASH.C1 — first login: show the landing-choice modal. Existing users
          // were backfilled to true in migration 0156, so only genuinely-new
          // signups hit this.
          destination = '/dashboard?firstLogin=true';
        } else {
          // DASH.C1 — route to the user's chosen landing surface. resolveLandingRoute
          // returns /projects for unknown/null, so this stays inside the fail-safe.
          destination = resolveLandingRoute(membership.defaultLandingPage, membership.lastProjectId ?? null);
        }
      } catch {
        // Fallback — never block login on failed membership check (also catches
        // an older server with no landing fields → /projects).
      }
      if (accessRevoked) {
        // An abandoned attempt does nothing — its refusal is about a session a
        // newer login may already have replaced (PR #3275 R2).
        if (!isCurrent()) return;
        // apiFetch's handler already cleared the stored credentials (when they
        // still held this token) and skipped its redirect because we are on
        // /login. Drop the in-memory session too — but only if it is still
        // the one THIS attempt installed (compare-and-reset) — and stay here.
        resetSession(installedToken);
        setError(TENANT_ACCESS_REVOKED_MESSAGE);
        return;
      }
      go(destination);
    } catch (err) {
      // An abandoned attempt shows nothing (PR #3275 R2). A login the provider
      // refused to install because a newer session change started is silent
      // too, even if this page still counts the attempt as current (PR #3275,
      // Codex bot).
      if (!isCurrent()) return;
      if (err instanceof LoginSupersededError) return;
      // #1975 (AC6) — the in-app recovery path for users who still verify.
      //
      // POST /auth/login answers an unverified account with 403 and a
      // structured body ({ error, requiresVerification, email }), but this
      // catch used to collapse everything to setError(err.message) — so the
      // user saw the bare string "Email not verified" with no way to act on
      // it. That was the whole of the "no in-app recovery" complaint: having
      // closed the post-registration tab, the only route back to a
      // verification email was the original one, which they no longer had.
      //
      // Route them to the pending page instead, which already knows how to
      // resend. `?redirect=` is carried through so a magic-link round-trip
      // still lands on the invitation after they verify.
      if (
        err instanceof ApiError &&
        err.status === 403 &&
        err.body?.requiresVerification === true
      ) {
        const unverifiedEmail = typeof err.body.email === 'string' ? err.body.email : email;
        const rawRedirect = searchParams.get('redirect');
        const redirect = isSafeRedirect(rawRedirect) ? rawRedirect : undefined;
        const verifyUrl = redirect
          ? `/verify-email?email=${encodeURIComponent(unverifiedEmail)}&redirect=${encodeURIComponent(redirect)}`
          : `/verify-email?email=${encodeURIComponent(unverifiedEmail)}`;
        go(verifyUrl);
        return;
      }
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-lg)',
        padding: 40,
      }}>
        {/* Brand header */}
        <div className="flex flex-col items-center mb-8">
          <BlueprintMark size={48} />
          <div className="flex items-baseline mt-3">
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>Blue</span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--primary)', letterSpacing: '-0.5px' }}>Print</span>
          </div>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
            AI-Powered SDLC Platform
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {resetSuccess && !error && (
            <div
              data-testid="login-reset-success"
              role="status"
              style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--success, #047857)', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', marginBottom: 16 }}
            >
              Your password has been updated. Sign in with your new password.
            </div>
          )}
          {error && (
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={inputStyle}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
              <label style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                Password
              </label>
              {/* ONB-FIX.D9 (#1081) — entry point to the password reset flow. */}
              <Link
                to="/forgot-password"
                style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
                onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
              >
                Forgot your password?
              </Link>
            </div>
            <PasswordInput
              required
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={inputStyle}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              display: 'block',
              width: '100%',
              height: 44,
              background: 'var(--primary)',
              color: 'white',
              fontFamily: 'var(--font-display)',
              fontSize: 15,
              fontWeight: 600,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'box-shadow 150ms',
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = 'var(--shadow-primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center" style={{ margin: '20px 0', gap: 12 }}>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </div>

        <OAuthButtons />

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 20 }}>
          Don&apos;t have an account?{' '}
          <Link
            to={(() => {
              // ONB.B2-FIX.D2 — forward ?redirect= to /register so the magic-link
              // round-trip survives the auth-page switch. Guard same-origin
              // before forwarding (defense-in-depth; LoginPage's sign-in path
              // already validates above).
              const redirect = searchParams.get('redirect');
              return isSafeRedirect(redirect)
                ? `/register?redirect=${encodeURIComponent(redirect)}`
                : '/register';
            })()}
            style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
          >
            Sign up
          </Link>
        </p>

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 32, opacity: 0.6 }}>
          © {new Date().getFullYear()} BluePrint · Built with ♥ by Ambai
        </p>
      </div>
    </div>
  );
}
